import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DateRangeValue = {
  startDate: string;
  endDate: string;
  label: string;
  preset: string;
  days: number;
};

type DatePreset = {
  label: string;
  value: string;
  days: number;
  offset?: number;
};

const DATE_PRESETS: DatePreset[] = [
  { label: "Today", value: "1d", days: 1 },
  { label: "Yesterday", value: "yesterday", days: 1, offset: 1 },
  { label: "Last 7 days", value: "7d", days: 7 },
  { label: "Last 14 days", value: "14d", days: 14 },
  { label: "Last 30 days", value: "30d", days: 30 },
];

const DATE_PRESET_LABEL_KEYS: Record<string, string> = {
  "1d": "date.today",
  yesterday: "date.yesterday",
  "7d": "date.last7Days",
  "14d": "date.last14Days",
  "30d": "date.last30Days",
};

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toStartOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, amount: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toDateKeyFromValue = (value: unknown) => {
  if (!value) return undefined;
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return undefined;
  return toDateKey(toStartOfDay(parsed));
};

const fromDateKey = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export const countRangeDays = (startDate: string, endDate: string) => {
  const start = fromDateKey(startDate).getTime();
  const end = fromDateKey(endDate).getTime();
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
};

const getLocaleDateCode = (locale: string) =>
  locale === "uk" ? "uk-UA" : "en-US";

const formatRangeDate = (value: string, locale: string) =>
  fromDateKey(value).toLocaleDateString(getLocaleDateCode(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatShortDate = (value: string, locale: string) =>
  fromDateKey(value).toLocaleDateString(getLocaleDateCode(locale), {
    month: "short",
    day: "numeric",
  });

export const getDateRangeForPreset = (
  value: string,
  today = new Date(),
): DateRangeValue => {
  const current =
    DATE_PRESETS.find((preset) => preset.value === value) ??
    DATE_PRESETS.find((preset) => preset.value === "7d")!;
  const end = addDays(toStartOfDay(today), -(current.offset ?? 0));
  const start = addDays(end, -(current.days - 1));
  return {
    startDate: toDateKey(start),
    endDate: toDateKey(end),
    label: current.label,
    preset: current.value,
    days: current.days,
  };
};

const getCalendarDays = (monthDate: Date) => {
  const firstOfMonth = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1,
  );
  const start = addDays(firstOfMonth, -firstOfMonth.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

type DateRangeFilterProps = {
  onTimeRangeChange: (value: string) => void;
  value: DateRangeValue;
  onValueChange: (value: DateRangeValue) => void;
  className?: string;
};

export function DateRangeFilter({
  onTimeRangeChange,
  value,
  onValueChange,
  className,
}: DateRangeFilterProps) {
  const { locale, t } = useLocale();
  const [open, setOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() =>
    fromDateKey(value.endDate),
  );

  useEffect(() => {
    if (!open) return;
    setPendingStart(null);
    setCalendarMonth(fromDateKey(value.endDate));
  }, [open]);

  const calendarDays = useMemo(
    () => getCalendarDays(calendarMonth),
    [calendarMonth],
  );
  const years = useMemo(() => {
    const selectedYear = calendarMonth.getFullYear();
    return Array.from({ length: 9 }, (_, index) => selectedYear - 4 + index);
  }, [calendarMonth]);

  const currentLabel =
    value.preset === "custom"
      ? `${formatShortDate(value.startDate, locale)} - ${formatShortDate(
          value.endDate,
          locale,
        )}`
      : t(DATE_PRESET_LABEL_KEYS[value.preset] || value.label);

  const selectPreset = (preset: DatePreset) => {
    const next = getDateRangeForPreset(preset.value);
    setPendingStart(null);
    onTimeRangeChange(preset.value === "yesterday" ? "1d" : preset.value);
    onValueChange(next);
    setCalendarMonth(fromDateKey(next.endDate));
  };

  const selectDate = (date: Date) => {
    const dateKey = toDateKey(date);

    if (!pendingStart) {
      setPendingStart(dateKey);
      const next = {
        startDate: dateKey,
        endDate: dateKey,
        label: "Custom",
        preset: "custom",
        days: 1,
      };
      onTimeRangeChange("1d");
      onValueChange(next);
      return;
    }

    const startDate = dateKey < pendingStart ? dateKey : pendingStart;
    const endDate = dateKey < pendingStart ? pendingStart : dateKey;
    const days = countRangeDays(startDate, endDate);
    const next = {
      startDate,
      endDate,
      label: "Custom",
      preset: "custom",
      days,
    };
    setPendingStart(null);
    onTimeRangeChange(`${days}d`);
    onValueChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-9 w-44 justify-start px-3 text-[#0A0A0A]",
            className,
          )}
          aria-label={t("legacy.select.date.range.jh1k6")}
        >
          <CalendarDays className="h-4 w-4" />
          <span className="min-w-0 flex-1 truncate text-left">
            {currentLabel}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex w-[428px] overflow-hidden rounded-lg border-[#E5E5E5] p-0 shadow-lg"
      >
        <div className="flex w-[248px] flex-col gap-4 p-3">
          <div className="flex h-8 items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md"
              onClick={() =>
                setCalendarMonth(
                  new Date(
                    calendarMonth.getFullYear(),
                    calendarMonth.getMonth() - 1,
                    1,
                  ),
                )
              }
              aria-label={t("legacy.previous.month.1k9t84k")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-1.5">
              <select
                value={calendarMonth.getMonth()}
                onChange={(event) =>
                  setCalendarMonth(
                    new Date(
                      calendarMonth.getFullYear(),
                      Number(event.target.value),
                      1,
                    ),
                  )
                }
                className="h-8 rounded-md border border-[#E5E5E5] bg-white px-2 text-sm font-medium outline-none"
                aria-label={t("legacy.select.month.9hgual")}
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              <select
                value={calendarMonth.getFullYear()}
                onChange={(event) =>
                  setCalendarMonth(
                    new Date(
                      Number(event.target.value),
                      calendarMonth.getMonth(),
                      1,
                    ),
                  )
                }
                className="h-8 rounded-md border border-[#E5E5E5] bg-white px-2 text-sm font-medium outline-none"
                aria-label={t("legacy.select.year.1okfl2a")}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-md"
              onClick={() =>
                setCalendarMonth(
                  new Date(
                    calendarMonth.getFullYear(),
                    calendarMonth.getMonth() + 1,
                    1,
                  ),
                )
              }
              aria-label={t("legacy.next.month.jq91u")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7">
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                className="flex h-[21px] w-8 items-center justify-center rounded-md text-xs text-[#737373]"
              >
                {day}
              </div>
            ))}
            {calendarDays.map((date) => {
              const dateKey = toDateKey(date);
              const isOutside = date.getMonth() !== calendarMonth.getMonth();
              const isStart = dateKey === value.startDate;
              const isEnd = dateKey === value.endDate;
              const isInRange =
                dateKey >= value.startDate && dateKey <= value.endDate;
              return (
                <button
                  key={dateKey}
                  type="button"
                  onClick={() => selectDate(date)}
                  className={cn(
                    "mt-2 flex h-8 w-8 items-center justify-center rounded-md text-sm leading-5 text-[#0A0A0A]",
                    isOutside && "opacity-50",
                    isInRange && "bg-[#FFC09C]/20",
                    (isStart || isEnd) && "bg-[#FF5E00] text-[#FAFAFA]",
                  )}
                  aria-pressed={isInRange}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex w-[180px] flex-col">
          <div className="flex flex-1 flex-col border-l border-[#E5E5E5] p-3">
            {DATE_PRESETS.map((preset) => {
              const selected = value.preset === preset.value;
              return (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => selectPreset(preset)}
                  className={cn(
                    "flex h-8 w-full items-center rounded-md px-2 text-left text-sm text-[#0A0A0A]",
                    selected && "bg-[#F5F5F5]",
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {t(DATE_PRESET_LABEL_KEYS[preset.value] || preset.label)}
                  </span>
                  {selected ? <Check className="h-4 w-4" /> : null}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => {
                onValueChange({ ...value, label: "Custom", preset: "custom" });
              }}
              className={cn(
                "flex h-8 w-full items-center rounded-md px-2 text-left text-sm text-[#0A0A0A]",
                value.preset === "custom" && "bg-[#F5F5F5]",
              )}
            >
              <span className="min-w-0 flex-1 truncate">
                <LocalizedText id="legacy.custom.1956hjq" />
              </span>
              {value.preset === "custom" ? <Check className="h-4 w-4" /> : null}
            </button>
          </div>
          <div className="flex h-10 items-center justify-center border-l border-t border-[#E5E5E5] px-3 text-xs text-[#737373]">
            <span className="truncate">
              {formatRangeDate(value.startDate, locale)} -{" "}
              {formatRangeDate(value.endDate, locale)}
            </span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
